/** Admin 展示用：IANA -> 运营可读时区名（印度市场默认 IST）。 */
const TIMEZONE_LABELS: Record<string, string> = {
  "Asia/Kolkata": "印度标准时间 (IST)",
};

/** 短标签，如「印度标准时间 (IST)」；未知时区回退 IANA。 */
export const formatTimezoneLabel = (tz: string): string => TIMEZONE_LABELS[tz] ?? tz;

/** 带 IANA 的完整说明，如「印度标准时间 (IST)（Asia/Kolkata）」。 */
export const formatTimezoneDetail = (tz: string): string => {
  const label = TIMEZONE_LABELS[tz];
  return label !== undefined ? `${label}（${tz}）` : tz;
};

/** 按 IANA 时区取当前自然日 YYYY-MM-DD（与 domain formatDayInTz 语义一致）。 */
export const todayInTz = (tz: string): string =>
  new Intl.DateTimeFormat("en-CA", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());

/** 趋势图 X 轴短标签：日 MM-DD；小时 MM-DD HH:00。 */
export const formatBucketChartLabel = (bucket: string, granularity: "day" | "hour"): string => {
  if (granularity === "day") {
    return bucket.length >= 10 ? bucket.slice(5) : bucket;
  }
  const m = /^(\d{4}-\d{2}-\d{2}) (\d{2}):00$/.exec(bucket);
  if (m !== null) return `${m[1]!.slice(5)} ${m[2]}:00`;
  return bucket;
};

/** ISO 时刻按指定时区格式化为运营可读时间（Admin 列表/详情用）。 */
export const formatDateTimeInTz = (iso: string, tz: string, locale = "zh-CN"): string => {
  try {
    return new Intl.DateTimeFormat(locale, {
      timeZone: tz,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).format(new Date(iso));
  } catch {
    return iso;
  }
};

/** 浏览器 / Viewer 时区（IANA）；失败回退 UTC。 */
export const getViewerTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
};

/** 指定 IANA 时区下的自然日 YYYY-MM-DD。 */
export const dayInTz = (tz: string, at: Date = new Date()): string =>
  new Intl.DateTimeFormat("en-CA", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(at);

/** 列表/Tooltip：业务时区 + 可选本地对照。 */
export const formatDateTimeTriple = (
  iso: string,
  businessTz: string,
  viewerTz?: string,
): { readonly business: string; readonly viewer: string; readonly utc: string } => {
  const business = formatDateTimeInTz(iso, businessTz);
  const viewer = formatDateTimeInTz(iso, viewerTz ?? getViewerTimezone());
  let utc = iso;
  try {
    utc = new Date(iso)
      .toISOString()
      .replace("T", " ")
      .replace(/\.\d{3}Z$/, " UTC");
  } catch {
    /* keep iso */
  }
  return { business, viewer, utc };
};

/** 短时区后缀：IST / UTC+4 等，用于表头旁标注。 */
export const formatTimezoneShort = (tz: string, at: Date = new Date()): string => {
  if (tz === "Asia/Kolkata") return "IST";
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      timeZoneName: "shortOffset",
    }).formatToParts(at);
    const name = parts.find((p) => p.type === "timeZoneName")?.value;
    return name ?? tz;
  } catch {
    return tz;
  }
};
